import { useEffect, useRef, useState, type CSSProperties, type Dispatch, type SetStateAction } from 'react';
import { color } from '../styles/theme';
import type { MeetingDetailResponse } from '../api/client';
import { useAskEna, type AskTurn } from '../api/meetings';
import { Button, Chip, ErrorNote, Modal, inputStyle } from '../components/ui';

/**
 * Ask Ena — ENACT's meeting assistant, for one meeting.
 *
 * A dialog off the page header: quick asks, a thread, a question box. Every
 * answer comes from the API (`POST /meetings/{id}/ask`), which reads the
 * meeting's own lines and nothing else, and saves nothing. The thread lives
 * with the page, so closing and reopening keeps the conversation until the
 * meeting is left. Where the API has no AI provider the dialog says so and
 * stays quiet, like the two helpers on the page.
 */

type Detail = MeetingDetailResponse;

const ASKS = ['Summarize everything', 'What is still open?', 'Who owns what?', 'What did we decide?'] as const;

const NOT_CONFIGURED = 'Ena is not configured on this API.';

const bubble: CSSProperties = {
  fontSize: 13.5,
  lineHeight: '20px',
  padding: '9px 13px',
  borderRadius: 14,
  maxWidth: '88%',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const enaLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: color.muted,
  paddingLeft: 4,
};

export function AskEna({
  d,
  thread,
  setThread,
  onClose,
}: {
  d: Detail;
  thread: AskTurn[];
  setThread: Dispatch<SetStateAction<AskTurn[]>>;
  onClose: () => void;
}) {
  const ask = useAskEna();
  const [question, setQuestion] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const configured = d.providers.ai.configured;

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [thread, ask.isPending]);

  useEffect(() => {
    // The dialog focuses its own panel on mount; a tick later the box wins.
    const t = window.setTimeout(() => box.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  async function send(raw: string) {
    const text = raw.trim();
    if (!text || ask.isPending || !configured) return;
    // The last turns come along so a follow-up ("and who owns that?") makes sense.
    const history = thread.slice(-10);
    setThread((t) => [...t, { role: 'user', text }]);
    setQuestion('');
    try {
      const res = await ask.mutateAsync({ id: d.meeting.id, question: text, history });
      setThread((t) => [...t, { role: 'assistant', text: res.answer }]);
    } catch {
      /* surfaced through ask.error, under the thread */
    }
  }

  return (
    <Modal
      label="Ask Ena"
      width={640}
      onClose={onClose}
      header={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden
            style={{
              display: 'flex',
              width: 30,
              height: 30,
              borderRadius: 9,
              background: color.tint2,
              color: color.blueDeep,
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              flex: 'none',
            }}
          >
            ✦
          </span>
          <span>
            <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: color.navy }}>Ask Ena</span>
            <span style={{ display: 'block', fontSize: 12, color: color.muted }}>
              About {d.meeting.title} · answers from this meeting's own lines, never a record
            </span>
          </span>
        </span>
      }
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={box}
              rows={2}
              value={question}
              disabled={!configured || ask.isPending}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                // Enter asks; Shift-Enter makes a new line; Escape with a draft stays here.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(question);
                } else if (e.key === 'Escape' && question.trim()) {
                  e.stopPropagation();
                }
              }}
              placeholder={configured ? 'Ask Ena about this meeting…' : NOT_CONFIGURED}
              aria-label="Ask Ena about this meeting"
              style={{ ...inputStyle, fontSize: 14, lineHeight: '20px', resize: 'none' }}
            />
            <Button
              variant="primary"
              onClick={() => void send(question)}
              disabled={!configured || ask.isPending || !question.trim()}
              title={!configured ? NOT_CONFIGURED : 'Enter asks too'}
            >
              {ask.isPending ? 'Asking…' : 'Ask'}
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, color: color.muted, flex: 1 }}>
              Nothing is saved. Each question is logged as an AI call, never its text.
            </span>
            {thread.length > 0 && (
              <button
                onClick={() => setThread([])}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: color.blue }}
              >
                Start over
              </button>
            )}
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 260 }}>
        {!configured && (
          <p style={{ fontSize: 12.5, margin: 0, padding: '8px 12px', borderRadius: 10, background: '#f4f0e6', color: '#7a6234' }}>
            {NOT_CONFIGURED} Set <code>AI_PROVIDER</code> and a key on the API and Ena answers here.
          </p>
        )}

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ASKS.map((a) => (
            <Chip key={a} label={a} active={false} onClick={() => void send(a)} title={configured ? 'Ask this' : NOT_CONFIGURED} />
          ))}
        </div>

        {thread.length === 0 && !ask.isPending ? (
          <p style={{ fontSize: 13, color: color.muted, margin: '6px 0 0' }}>
            Ask about what was said, what is still open, who owns what, or what was decided. Ena reads the agenda,
            the notes in the room, what carried over and the saved summary — and says when the notes do not say.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {thread.map((t, i) =>
              t.role === 'user' ? (
                <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ ...bubble, background: color.navy, color: color.white }}>{t.text}</span>
                </div>
              ) : (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
                  <span style={enaLabel}>Ena</span>
                  <span style={{ ...bubble, background: color.tint, color: color.ink }}>{t.text}</span>
                </div>
              ),
            )}
            {ask.isPending && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
                <span style={enaLabel}>Ena</span>
                <span style={{ ...bubble, background: color.tint, color: color.muted }}>Reading the meeting…</span>
              </div>
            )}
            {ask.error && <ErrorNote message={(ask.error as Error).message} />}
            <div ref={end} />
          </div>
        )}
      </div>
    </Modal>
  );
}
